import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Printer, DownloadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import html2pdf from "html2pdf.js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GRESART_HEADER_BG = "https://customer-assets.emergentagent.com/job_client-products/artifacts/si9oqnwo_image.png";
const GRESART_FOOTER_BG = "https://customer-assets.emergentagent.com/job_client-products/artifacts/mjcj90xi_image.png";
const GRESART_LOGO = "https://customer-assets.emergentagent.com/job_client-products/artifacts/3ol4ta6t_image.png";

export default function Reports() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const reportRef = useRef(null);

  const fetchReport = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports/client/${clientId}`);
      setReport(response.data);
    } catch (error) {
      console.error("Error fetching report:", error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!report) return;
    
    setGenerating(true);
    try {
      // Use server-side PDF generation
      const response = await axios.get(`${API}/reports/client/${clientId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const clientName = report.client.name.replace(/[^a-zA-Z0-9]/g, '_');
      const date = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `Relatorio_${clientName}_${date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Erro ao gerar PDF. Por favor tente novamente.");
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-16">
        <p className="text-stone-500">Não foi possível gerar o relatório</p>
        <Link to="/clients" className="btn-primary mt-4 inline-flex">
          <ArrowLeft size={18} />
          Voltar a Clientes
        </Link>
      </div>
    );
  }

  // Group products by type
  const productsByType = report.products.reduce((acc, product) => {
    const type = product.product_type || "OUTROS";
    if (!acc[type]) acc[type] = [];
    acc[type].push(product);
    return acc;
  }, {});

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="reports-page">
      {/* Header - No print */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print">
        <PageHeader
          title="Relatório"
          subtitle={report.client.name}
          onBack={() => navigate(`/clients/${clientId}`)}
        />
        <div className="flex gap-2">
          <Button variant="outline" className="btn-secondary flex-1 sm:flex-none" onClick={handlePrint} data-testid="btn-print">
            <Printer size={18} />
            <span className="sm:inline">Imprimir</span>
          </Button>
          <Button className="btn-primary flex-1 sm:flex-none" onClick={handleDownloadPDF} disabled={generating} data-testid="btn-download-pdf">
            <DownloadSimple size={18} />
            {generating ? "A gerar..." : <span>PDF</span>}
          </Button>
        </div>
      </div>

      {/* Report Container - Print friendly */}
      <div ref={reportRef} className="bg-white shadow-lg rounded-lg overflow-hidden print-container" data-testid="report-container">
        
        {/* Header with Logo */}
        <div className="report-header relative bg-[#009392]">
          <div className="flex items-center justify-between px-4 sm:px-8 py-3">
            <img 
              src={GRESART_HEADER_BG}
              alt="GRESART Header" 
              className="h-8 sm:h-10 w-auto object-contain"
            />
            <div className="text-right text-white">
              <h2 className="text-xs sm:text-base font-bold tracking-wide">RELATÓRIO DE MERCHANDISING</h2>
              <p className="text-white/80 text-[10px] sm:text-xs">Produtos em Showroom</p>
            </div>
          </div>
        </div>

        {/* Client Info Section */}
        <div className="px-4 sm:px-8 py-4 sm:py-6 bg-stone-50 border-b border-stone-200">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
            {report.client.logo_url ? (
              <img 
                src={report.client.logo_url} 
                alt={`Logo ${report.client.name}`}
                className="w-20 h-20 sm:w-28 sm:h-28 object-contain border-2 border-stone-200 rounded-xl bg-white flex-shrink-0 shadow-sm"
              />
            ) : (
              <div className="w-20 h-20 sm:w-28 sm:h-28 bg-gradient-to-br from-stone-50 to-stone-100 border-2 border-stone-200 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                <span className="text-3xl sm:text-4xl font-bold text-stone-300">{report.client.name.charAt(0)}</span>
              </div>
            )}
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <h3 className="text-xl sm:text-2xl font-bold font-[Manrope] text-stone-900 mb-2 sm:mb-3">
                {report.client.name}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
                <div className="space-y-1">
                  {(report.client.contacts?.[0]?.name || report.client.contact_person) && (
                    <p className="text-stone-600"><span className="text-stone-400 font-medium">Contacto:</span> {report.client.contacts?.[0]?.name || report.client.contact_person}{report.client.contacts?.[0]?.cargo ? ` (${report.client.contacts[0].cargo})` : ''}</p>
                  )}
                  {(report.client.contacts?.[0]?.phone || report.client.phone) && (
                    <p className="text-stone-600"><span className="text-stone-400 font-medium">Tel:</span> {report.client.contacts?.[0]?.phone || report.client.phone}</p>
                  )}
                  {report.client.email && (
                    <p className="text-stone-600"><span className="text-stone-400 font-medium">Email:</span> {report.client.email}</p>
                  )}
                </div>
                <div className="space-y-1">
                  {report.client.address && (
                    <p className="text-stone-600">{report.client.address}</p>
                  )}
                  {(report.client.city || report.client.country) && (
                    <p className="text-stone-600">
                      {[report.client.city, report.client.country].filter(Boolean).join(", ")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Total and Date - Responsive */}
          <div className="flex flex-col sm:flex-row sm:justify-end items-center gap-3 sm:gap-6 mt-4 pt-4 border-t border-stone-200">
            <div className="flex gap-4 text-sm text-stone-400">
              <p>Atualizado: {formatDate(report.generated_at)}</p>
              <p>{report.products.length} produtos</p>
            </div>
            {report.client.total_value > 0 && (
              <div className="bg-[#00A19A]/10 px-4 py-2 rounded-lg text-center">
                <span className="text-sm text-stone-500 mr-2">Total Investido:</span>
                <span className="text-xl font-bold text-[#00A19A]">
                  €{report.client.total_value.toFixed(2)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Products by Type */}
        <div className="px-4 sm:px-8 py-4 sm:py-6">
          {Object.entries(productsByType).map(([type, products]) => (
            <div key={type} className="mb-8 sm:mb-10 last:mb-0">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 pb-2 sm:pb-3 border-b-2 border-[#00A19A]">
                <div className="w-1 sm:w-1.5 h-6 sm:h-8 bg-[#00A19A] rounded-full"></div>
                <h4 className="text-base sm:text-xl font-bold font-[Manrope] text-stone-800 uppercase tracking-wide">
                  {type}
                </h4>
                <span className="text-xs sm:text-sm text-stone-400 bg-stone-100 px-2 sm:px-3 py-1 rounded-full">
                  {products.length} {products.length === 1 ? 'produto' : 'produtos'}
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
                {products.map((product) => (
                  <div 
                    key={product.id || product.code} 
                    className="border border-stone-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-200" 
                    data-testid={`report-product-${product.id || product.code}`}
                  >
                    <div className="bg-stone-50 p-2 sm:p-4">
                      <img 
                        src={product.image_url} 
                        alt={product.code}
                        className="w-full h-auto object-contain"
                      />
                    </div>
                    <div className="p-2 sm:p-3 bg-white">
                      <p className="font-mono text-xs sm:text-sm font-bold text-stone-800">{product.code}</p>
                      {product.secondary_code && (
                        <p className="text-[10px] sm:text-xs text-stone-400 font-mono">{product.secondary_code}</p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1 sm:mt-2">
                        {product.collection && (
                          <span className="text-[10px] sm:text-xs bg-[#00A19A]/10 text-[#00A19A] px-1.5 sm:px-2 py-0.5 rounded font-medium">
                            {product.collection}
                          </span>
                        )}
                        {product.color && (
                          <span className="text-[10px] sm:text-xs bg-stone-100 text-stone-600 px-1.5 sm:px-2 py-0.5 rounded">
                            {product.color}
                          </span>
                        )}
                        {product.size && (
                          <span className="text-[10px] sm:text-xs bg-stone-100 text-stone-600 px-1.5 sm:px-2 py-0.5 rounded">
                            {product.size}
                          </span>
                        )}
                      </div>
                      {product.value && (
                        <p className="text-sm sm:text-base font-bold text-[#00A19A] mt-1 sm:mt-2">€{parseFloat(product.value).toFixed(2)}</p>
                      )}
                      {product.quantity > 1 && (
                        <p className="text-[10px] sm:text-xs text-stone-500 font-medium mt-1">
                          Qtd: {product.quantity}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {report.products.length === 0 && (
            <div className="text-center py-8 sm:py-12 text-stone-400">
              <p className="text-base sm:text-lg">Não há produtos atribuídos a este showroom</p>
            </div>
          )}
        </div>

        {/* Showroom Images Section */}
        {report.client.showroom_images && report.client.showroom_images.length > 0 && (
          <div className="px-4 sm:px-8 py-4 sm:py-6 border-t border-stone-200">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 pb-2 border-b-2 border-[#00A19A]">
              <div className="w-1 sm:w-2 h-5 sm:h-6 bg-[#00A19A] rounded-full"></div>
              <h4 className="text-base sm:text-lg font-bold font-[Manrope] text-stone-800 uppercase tracking-wide">
                Showroom
              </h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
              {report.client.showroom_images.map((img, idx) => (
                <div key={`showroom-${idx}`} className="rounded-lg overflow-hidden border border-stone-200 bg-stone-50">
                  <img src={img} alt={`Showroom ${idx + 1}`} className="w-full h-auto object-contain" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ambiente Images Section */}
        {report.client.ambiente_images && report.client.ambiente_images.length > 0 && (
          <div className="px-4 sm:px-8 py-4 sm:py-6 border-t border-stone-200">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 pb-2 border-b-2 border-[#00A19A]">
              <div className="w-1 sm:w-2 h-5 sm:h-6 bg-[#00A19A] rounded-full"></div>
              <h4 className="text-base sm:text-lg font-bold font-[Manrope] text-stone-800 uppercase tracking-wide">
                Ambientes
              </h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
              {report.client.ambiente_images.map((img, idx) => (
                <div key={`ambiente-${idx}`} className="rounded-lg overflow-hidden border border-stone-200 bg-stone-50">
                  <img src={img} alt={`Ambiente ${idx + 1}`} className="w-full h-auto object-contain" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes Section */}
        {report.client.notes && (
          <div className="px-4 sm:px-8 py-4 sm:py-6 border-t border-stone-200">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 pb-2 border-b-2 border-[#00A19A]">
              <div className="w-1 sm:w-2 h-5 sm:h-6 bg-[#00A19A] rounded-full"></div>
              <h4 className="text-base sm:text-lg font-bold font-[Manrope] text-stone-800 uppercase tracking-wide">
                Notas
              </h4>
            </div>
            <div className="bg-stone-50 rounded-lg p-3 sm:p-4">
              <p className="text-stone-700 whitespace-pre-wrap text-sm sm:text-base">{report.client.notes}</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="report-footer bg-[#009392] px-4 sm:px-8 py-2 sm:py-3">
          <div className="flex items-center justify-between text-white">
            <span className="text-xs sm:text-sm font-medium">www.gresart.com</span>
            <span className="text-xs sm:text-sm font-medium">Gresart - Cerâmica Industrial, S.A.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
